// utils/memory/mongoMemoryUtils.js

import axios from '../../api/axiosInstance';
import { API_BASE } from '../../api/config';
import { debugLog } from '../logging/debugLog';

export async function getListFromMongo(shadowId, listName) {
  try {
    if (listName && listName.toLowerCase().includes('grocery')) {
      listName = 'grocery list';
    }

    const res = await axios.get(`${API_BASE}/api/memory/mongo/get-list`, {
      params: { shadowId, listName },
    });

    return res.data;
  } catch (err) {
    console.error('❌ [getListFromMongo] Failed:', err.response?.data || err.message);
    return null;
  }
}


export async function fetchSessionRelationshipContext() {
  try {
    const res = await axios.get(`${API_BASE}/api/session/init`);

    const relationships = Array.isArray(res.data?.relationships)
      ? res.data.relationships
      : [];

    const relationshipNetwork = {
      edges: Array.isArray(res.data?.relationshipNetwork?.edges)
        ? res.data.relationshipNetwork.edges
        : [],
    };

    debugLog('🧭 [SessionRelationshipContext] Fetched:', {
      relationshipCount: relationships.length,
      relationshipEdgeCount: relationshipNetwork.edges.length,
    });

    return {
      relationships,
      relationshipNetwork,
    };
  } catch (err) {
    console.error(
      '❌ [SessionRelationshipContext] Failed:',
      err.response?.data || err.message
    );

    return null;
  }
}

export async function fetchRelationships() {
  try {
    const res = await axios.get(`${API_BASE}/api/memory/mongo/relationships`);

    const relationships = Array.isArray(res.data?.relationships)
      ? res.data.relationships
      : Array.isArray(res.data)
      ? res.data
      : [];

    debugLog('🧑‍🤝‍🧑 Relationships fetched:', relationships);

    relationships.forEach((person) => {
      debugLog('🧾 [RelationshipState]', {
        first_name: person?.first_name || null,
        last_name: person?.last_name || null,
        relationship: person?.relationship || null,
        lifeState: person?.lifeState || 'unknown',
        healthState: person?.healthState || 'unknown',
        location: person?.location || null,
        source: person?.source || null,
        isActive: person?.isActive,
      });
    });

    return relationships;
  } catch (err) {
    console.error('❌ Failed to fetch relationships:', err.response?.data || err.message);
    return [];
  }
}

export async function upsertRelationshipState({
  entityId = null,
  first_name,
  last_name = null,
  relationship,
  lifeState,
  healthState,
  location,
  notes = null,
  source = 'conversation',
  isActive = true,
  includeGraphResult = false,
}) {
  try {
    const payload = {
      entityId,
      first_name,
      last_name,
      relationship,
      source,
      isActive,
    };

    if (lifeState) payload.lifeState = lifeState;
    if (healthState) payload.healthState = healthState;
    if (location) payload.location = location;
    if (notes) payload.notes = notes;

    debugLog('🛰️ [upsertRelationshipState] Sending payload:', payload);

    const res = await axios.post(
      `${API_BASE}/api/memory/mongo/relationships/upsert`,
      payload
    );

    debugLog('✅ [upsertRelationshipState] Success:', res.data);

    if (includeGraphResult) {
      return {
        relationship: res.data?.relationship || null,
        graphEdge: res.data?.graphEdge || null,
      };
    }

    return res.data?.relationship || null;
  } catch (err) {
    console.error(
      '❌ [upsertRelationshipState] Failed:',
      err.response?.data || err.message
    );
    return null;
  }
}

export async function fetchRelationshipNetwork() {
  try {
    const res = await axios.get(`${API_BASE}/api/memory/mongo/relationship-network`);

    const network = {
      edges: Array.isArray(res.data?.edges) ? res.data.edges : [],
    };

    debugLog('🕸️ [RelationshipNetwork] Fetched:', network);

    return network;
  } catch (err) {
    console.warn(
      '⚠️ [RelationshipNetwork] Failed to fetch:',
      err.response?.data || err.message
    );

    return {
      edges: [],
    };
  }
}

export async function upsertRelationshipNetworkEdge({
  fromName,
  fromType = 'unknown',
  fromEntityId = null,
  toName,
  toType = 'unknown',
  toEntityId = null,
  relation,
  status = 'observed',
  confidence = 0,
  resolutionConfidence = 0,
  relationStability = 'unknown',
  authority = null,
  source = 'relationship_network_candidate',
  sourceText = null,
  evidenceCount = 1,
  isActive = true,
  metadata = null,
}) {
  try {
    const payload = {
      fromName,
      fromType,
      fromEntityId,
      toName,
      toType,
      toEntityId,
      relation,
      status,
      confidence,
      resolutionConfidence,
      relationStability,
      authority,
      source,
      evidenceCount,
      isActive,
    };

    if (sourceText) payload.sourceText = sourceText;
    if (metadata) payload.metadata = metadata;

    debugLog('🕸️ [RelationshipNetworkEdge] Sending payload:', payload);

    const res = await axios.post(
      `${API_BASE}/api/memory/mongo/relationship-network/edges/upsert`,
      payload
    );

    debugLog('✅ [RelationshipNetworkEdge] Success:', res.data);

    return res.data?.edge || null;
  } catch (err) {
    console.warn(
      '⚠️ [RelationshipNetworkEdge] Failed:',
      err.response?.data || err.message
    );

    return null;
  }
}